/**
 * Source-frame URL derivation — pure logic for the Screen Viewer hook.
 *
 * This is the static input/output mapping that `useSourceFrame` (in
 * `@polygraf/client`) wraps with React state + interval timers. Lives in
 * shared so the URL-derivation logic can be unit-tested in isolation
 * without depending on a React test runtime.
 *
 * See ADR-0005 for the routing rationale; the temporal layer (polled vs
 * streaming, cache-busting, isPlaying gating) lives in the hook.
 */
import { ScreenFormat } from './types.js';
import { buildViewerSourceUrl } from './viewer-url.js';
function isPolledFormat(format) {
    return format === ScreenFormat.JPG;
}
function isStreamingFormat(format) {
    return (format === ScreenFormat.MJPG ||
        format === ScreenFormat.MP4 ||
        format === ScreenFormat.HLS ||
        // RTSP / NDI services produce a directly-fetchable URL via the
        // MediaMTX-backed cloud-proxy path on the server. From the client's
        // perspective they look like HLS — the manifest is m3u8 and the
        // `<video>` element streams it. The "needs a sourceId" rule below
        // applies (no ad-hoc rtsp:// or ndi:// URLs in URL-param mode).
        format === ScreenFormat.RTSP ||
        format === ScreenFormat.NDI);
}
/**
 * Formats that only produce a fetchable URL when we have a persisted Source
 * id (so the request can route through `/api/proxy/service/:id` where
 * MediaMTX-backed transcoding lives). Ad-hoc URL-param mode for these
 * formats returns null because the generic format-keyed proxy endpoints
 * can't transcode rtsp:// or ndi:// URLs.
 */
function requiresSourceId(format) {
    return format === ScreenFormat.RTSP || format === ScreenFormat.NDI;
}
/**
 * Compute the base URL (without cache-buster) the Screen Viewer should
 * fetch for the given Source + format. Pure. Returns `null` when no URL
 * can be produced — the consumer renders the "format not supported" or
 * empty state for null returns.
 *
 * Null cases:
 *   - source is null or has no endpoint
 *   - format is unsupported altogether
 *   - format is RTSP / NDI in LAN-direct or ad-hoc URL-param mode
 *     (those need the server-side MediaMTX detour)
 */
export function deriveSourceFrameBaseUrl(input) {
    const { source, format, useProxy, sourceId, urlParam, apiUrl, mjpgFramePoll } = input;
    if (!source?.endpoint)
        return null;
    // Formats outside the supported set produce no URL.
    if (!isPolledFormat(format) && !isStreamingFormat(format))
        return null;
    // RTSP / NDI: the only way to render is the MediaMTX-backed cloud proxy.
    // Without a persisted sourceId, or with proxying disabled, we can't
    // produce a URL the browser knows how to fetch.
    if (requiresSourceId(format) && (!useProxy || !sourceId || !!urlParam)) {
        return null;
    }
    if (mjpgFramePoll && format === ScreenFormat.MJPG && useProxy && sourceId && !urlParam) {
        return `${apiUrl}/api/proxy/service/${encodeURIComponent(sourceId)}/frame`;
    }
    return buildViewerSourceUrl({
        // Ad-hoc mode (`urlParam` set) intentionally bypasses sourceId — the
        // generic format-keyed proxy is the right target for one-off URLs.
        sourceId: sourceId && !urlParam ? sourceId : undefined,
        rawUrl: source.endpoint,
        format,
        useProxy,
        apiUrl,
    });
}
/**
 * Whether a given format is polled (the hook drives a refresh interval) or
 * streaming (URL emitted once; multipart / `<video>` element owns updates).
 */
export function isPolledSourceFormat(format) {
    return isPolledFormat(format);
}
export function isStreamingSourceFormat(format) {
    return isStreamingFormat(format);
}
/**
 * Whether `url` is a single-frame URL of a service (`/api/proxy/service/:id/frame`),
 * with or without its cache-buster. The viewer fetches a frame-polled MJPG with the
 * app's bearer only once its URL is one of these: for one render after the switch
 * the URL is still the stream's, and a bearer fetch of it would open one more
 * upstream connection for nothing (review of #857).
 */
export function isServiceFrameUrl(url) {
    if (!url)
        return false;
    let path;
    try {
        path = new URL(url, 'http://relative.invalid').pathname;
    }
    catch {
        return false;
    }
    return /^\/api\/proxy\/service\/[^/]+\/frame$/.test(path);
}
//# sourceMappingURL=source-frame-url.js.map